import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, withModifiers as _withModifiers, withKeys as _withKeys, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex justify-flex-start items-center" }
const _hoisted_2 = { class: "mr-4 flex items-center justify-center h-12 w-12 rounded-full bg-primary-color-100" }
const _hoisted_3 = { class: "grid grid-cols-2 gap-4" }
const _hoisted_4 = { class: "grid grid-cols-2 gap-4" }
const _hoisted_5 = { class: "flex justify-end" }

import { computed, ref, watch, nextTick } from "vue"
import { useRoute } from "vue-router"
import { DialogTitle } from "@headlessui/vue"
import GgmsButton from "@/components/GgmsButton.vue"
import GgmsModal from "@/components/GgmsModal.vue"
import GgmsInput from "@/components/GgmsInput.vue"
import GgmsPhoneTelInput from "@/components/GgmsPhoneTelInput.vue"
import GgmsDropdown from "@/components/GgmsDropdown.vue"
import GgmsMultiSelect from "@/components/GgmsMultiSelect.vue"
import { UserAddIcon } from "@heroicons/vue/outline"
import { useContactsStore } from "@/stores/contacts"
import { useGridCollectionStore } from "@/stores/grid-collection"
import { useTagStore } from "@/stores/tag"
import { useAgencyStore } from "@/stores/agency"
import { useOriginStore } from "@/stores/origin"
import { useLifecycleStageStore } from "@/stores/lifecycle-stage"
import { useContactRoleStore } from "@/stores/contact-role"
import { useSalesStatusStore } from "@/stores/sales-status"
import { usePhoneCallStore } from "@/stores/phone-call"
import { ToastService } from "@/shared/services/toast"
import { Phone } from "@/shared/models/phone"
import { isInvalid, hasErrorMessage, getDomainId } from "@/shared/utils/helpers"
import { isValidPhoneNumber } from "libphonenumber-js"
import { Contact, CreateContactResponse } from "@/shared/models/contact"
import { GgmsError } from "@/shared/services/client"
import { Tag } from "@/shared/models/tag"
import { PHONE_STATUS } from "@/shared/models/phone-number"
import { LifecycleStage } from "@/shared/models/lifecycle-stage"
import { ContactRole } from "@/shared/models/contact-role"
import { SalesStatus } from "@/shared/models/sales-status"

interface Props {
    open: boolean
    contact?: Partial<Contact>
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AddContactModal',
  props: {
    open: { type: Boolean },
    contact: {}
  },
  emits: ["closeModal"],
  setup(__props: any, { emit: __emit }) {

const phoneCallStore = usePhoneCallStore()
const originStore = useOriginStore()
const contactsStore = useContactsStore()
const gridCollectionStore = useGridCollectionStore()
const tagStore = useTagStore()
const agencyStore = useAgencyStore()
const lifecycleStageStore = useLifecycleStageStore()
const contactRoleStore = useContactRoleStore()
const salesStatusStore = useSalesStatusStore()
const toastService = new ToastService()
const route = useRoute()

const props = __props

const emit = __emit

const collections = computed(() => gridCollectionStore.collectionsResponse)

const firstName = ref()
const lastName = ref()
const email = ref()
const phone = ref({ country: "RO", number: "" } as Phone)
const origin = ref([])
const tag = ref([])
const lifecycleStage = ref([])
const contactRole = ref([])
const salesStatus = ref([])

const error = ref()
const errorField = ref("")
const isLoading = ref(false)
const origins = computed(() => originStore.tableState.data || [])
const tags = ref<Tag[]>([])
const lifecycleStages = ref<LifecycleStage[]>([])
const contactRoles = ref<ContactRole[]>([])
const salesStatuses = ref<SalesStatus[]>([])

const cachedLifecycleStages = computed(() => lifecycleStageStore.cachedLifecycleStages)
const cachedContactRoles = computed(() => contactRoleStore.cachedRoles)
const cachedSalesStatuses = computed(() => salesStatusStore.cachedSalesStatuses)
const cachedTags = computed(() => tagStore.cachedTags)
const cachedOrigins = computed(() => originStore.cachedOrigins)

const isPhoneInputReadOnly = computed(() => !!props.contact?.phone?.number)

const selectedDomain = ref(initializeDomain())
const domainOptions = computed(() => agencyStore.agency.agencyDomains)

function handleEnterKey(event: KeyboardEvent) {
    if (event.key === "Enter" && contactsStore.isAddContactModalOpen) {
        addContact()
    }
}

async function addContact() {
    try {
        isLoading.value = true
        error.value = undefined

        if (phone.value?.number && !isValidPhoneNumber(phone.value?.number, phone.value.country)) {
            error.value = {
                field: "phone.number",
                message: "Invalid phone number",
            }
            isLoading.value = false
            return
        }

        if (!selectedDomain.value) {
            nextTick(() => {
                error.value = {
                    field: "domain",
                    message: "Domain is required. Please select a domain.",
                }
            })
            isLoading.value = false
            return
        }
        localStorage.setItem("contactDomainId", selectedDomain.value)

        //lookup for phone number
        if (phone.value.number) {
            const lookupResponse = await phoneCallStore.lookupPhoneNumber(phone.value.number)
            if (lookupResponse.status === "NOK") {
                console.warn(lookupResponse.message)
            }
            if (lookupResponse) {
                phone.value.lineType = lookupResponse.type
                phone.value.status =
                    lookupResponse.carrier_name && !lookupResponse.error ? PHONE_STATUS.ACTIVE : PHONE_STATUS.UNKNOWN
            }
        }

        const payload = {
            firstName: firstName.value?.trim() || undefined,
            lastName: lastName.value?.trim() || undefined,
            email: email.value ? { emailAddress: email.value?.trim(), isPrimary: true } : undefined,
            phone: phone.value?.number
                ? ({
                      country: phone.value.country,
                      number: phone.value.number,
                      lineType: phone.value.lineType,
                      status: phone.value.status,
                      label: "primary",
                  } as Phone)
                : undefined,
            origins: origin.value?.length ? origin.value.map((id) => cachedOrigins.value[id]) : undefined,
            tags: tag.value?.length ? tag.value.map((id) => cachedTags.value[id]) : undefined,
            lifecycleStages: lifecycleStage.value?.length
                ? lifecycleStage.value.map((id) => cachedLifecycleStages.value[id])
                : undefined,
            roles: contactRole.value?.length ? contactRole.value.map((id) => cachedContactRoles.value[id]) : undefined,
            salesStatuses: salesStatus.value?.length
                ? salesStatus.value.map((id) => cachedSalesStatuses.value[id])
                : undefined,
        }

        const response = props.contact?._id
            ? await contactsStore.updateContact(props.contact._id, payload)
            : await contactsStore.addContact(payload)

        localStorage.removeItem("contactDomainId")

        if (!response) {
            return
        }

        toastService.addToast({
            type: "success",
            message: (response as CreateContactResponse)?.updatedExisting
                ? "There was already a contact that was updated with the new details."
                : "A new contact was created.",
        })

        isLoading.value = false

        gridCollectionStore.collectionType = "contact"
        await gridCollectionStore.getCollections()

        if (route.name === "contacts") {
            if (gridCollectionStore.selectedTabId) {
                contactsStore.tableState.ids = collections.value
                    .find(
                        (collectionResponse) => collectionResponse.collection._id === gridCollectionStore.selectedTabId
                    )
                    ?.ids.join(",")
            }

            await contactsStore.loadContacts(!!contactsStore.tableState.filtersGroups?.length, true)
        }

        closeModal()
    } catch (e) {
        nextTick(() => {
            const err = e as GgmsError
            error.value = err
            if (["email", "email.emailaddress"].includes(err.field as string)) {
                errorField.value = err.field as string
            }
        })
        localStorage.removeItem("contactDomainId")
    } finally {
        isLoading.value = false
    }
}

function closeModal() {
    emit("closeModal")
    firstName.value = undefined
    lastName.value = undefined
    email.value = undefined
    phone.value = { country: "RO", number: "" }
    origin.value = []
    tag.value = []
    lifecycleStage.value = []
    contactRole.value = []
    salesStatus.value = []
    error.value = undefined
    selectedDomain.value = initializeDomain()
}

function initializeDomain() {
    const domain = getDomainId()
    return domain === "all" ? null : domain
}

watch(
    () => agencyStore.selectedDomain,
    (newValue) => {
        selectedDomain.value = newValue._id === "all" ? "" : newValue._id
    }
)

watch(
    () => props.open,
    async (value) => {
        if (value) {
            tags.value = await tagStore.getTagsForContacts()
            await Promise.all([lifecycleStageStore.getAll(), contactRoleStore.getAll(), salesStatusStore.getAll()])
            lifecycleStages.value = lifecycleStageStore.tableState.data
            contactRoles.value = contactRoleStore.tableState.data
            salesStatuses.value = salesStatusStore.tableState.data
        }

        if (!value || !props.contact) {
            return
        }
        phone.value.number = props.contact?.phone?.number || ""
        phone.value.country = props.contact?.phone?.country || "RO"
    }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(GgmsModal, {
    open: props.open,
    onCloseModal: closeModal,
    onKeydown: _withKeys(_withModifiers(handleEnterKey, ["prevent"]), ["enter"])
  }, {
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_unref(UserAddIcon), {
            class: "h-6 w-6 text-primary-color-600",
            "aria-hidden": "true"
          })
        ]),
        _createVNode(_unref(DialogTitle), {
          as: "h3",
          class: "text-lg leading-6 font-medium text-gray-900"
        }, {
          default: _withCtx(() => _cache[10] || (_cache[10] = [
            _createTextVNode("Add Contact")
          ])),
          _: 1,
          __: [10]
        })
      ])
    ]),
    under: _withCtx(() => [
      _createElementVNode("div", _hoisted_5, [
        _createVNode(GgmsButton, {
          styleType: "secondary",
          onClick: closeModal
        }, {
          default: _withCtx(() => _cache[11] || (_cache[11] = [
            _createTextVNode("Cancel")
          ])),
          _: 1,
          __: [11]
        }),
        _createVNode(GgmsButton, {
          class: "ml-3",
          type: "submit",
          disabled: isLoading.value,
          onClick: addContact
        }, {
          default: _withCtx(() => _cache[12] || (_cache[12] = [
            _createTextVNode("Add contact")
          ])),
          _: 1,
          __: [12]
        }, 8, ["disabled"])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("form", {
        class: "flex flex-col gap-y-[30px]",
        onSubmit: _withModifiers(addContact, ["prevent"])
      }, [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(GgmsInput, {
            modelValue: firstName.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((firstName).value = $event)),
            name: "first-name",
            label: "First name",
            required: true,
            invalid: _unref(isInvalid)('firstname', error.value),
            errorMessage: _unref(hasErrorMessage)('firstname', error.value)
          }, null, 8, ["modelValue", "invalid", "errorMessage"]),
          _createVNode(GgmsInput, {
            modelValue: lastName.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((lastName).value = $event)),
            name: "last-name",
            label: "Last name",
            invalid: _unref(isInvalid)('lastname', error.value),
            errorMessage: _unref(hasErrorMessage)('lastname', error.value)
          }, null, 8, ["modelValue", "invalid", "errorMessage"])
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(GgmsInput, {
            modelValue: email.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((email).value = $event)),
            type: "email",
            name: "email",
            label: "Email address",
            required: true,
            invalid: _unref(isInvalid)(errorField.value, error.value),
            errorMessage: _unref(hasErrorMessage)(errorField.value, error.value)
          }, null, 8, ["modelValue", "invalid", "errorMessage"]),
          _createVNode(GgmsPhoneTelInput, {
            modelValue: phone.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((phone).value = $event)),
            name: "phone",
            label: "Cell phone",
            placeholder: "Enter phone number",
            required: true,
            readonly: isPhoneInputReadOnly.value,
            invalid: _unref(isInvalid)('phone.number', error.value),
            errorMessage: _unref(hasErrorMessage)('phone.number', error.value)
          }, null, 8, ["modelValue", "readonly", "invalid", "errorMessage"])
        ]),
        _createVNode(GgmsDropdown, {
          modelValue: selectedDomain.value,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((selectedDomain).value = $event)),
          options: domainOptions.value,
          name: "domain",
          label: "Domain",
          "is-show-clear": true,
          optionLabel: "url.domain",
          optionValue: "_id",
          placeholder: "Select Domain",
          class: "w-full",
          invalid: _unref(isInvalid)('domain', error.value),
          "error-message": _unref(hasErrorMessage)('domain', error.value)
        }, null, 8, ["modelValue", "options", "invalid", "error-message"]),
        _createVNode(GgmsMultiSelect, {
          modelValue: origin.value,
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((origin).value = $event)),
          label: "Origins",
          placeholder: "Select a contact origin",
          display: "chip",
          options: origins.value,
          optionLabel: "displayName",
          optionValue: "_id",
          classes: "w-full",
          "max-height": 110
        }, null, 8, ["modelValue", "options"]),
        _createVNode(GgmsMultiSelect, {
          modelValue: tag.value,
          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((tag).value = $event)),
          label: "Tags",
          placeholder: "+ Add Tag",
          display: "chip",
          options: tags.value,
          optionLabel: "displayName",
          optionValue: "_id",
          invalid: _unref(isInvalid)('tags', error.value),
          errorMessage: _unref(hasErrorMessage)('tags', error.value),
          classes: "w-full",
          "open-direction": "above"
        }, null, 8, ["modelValue", "options", "invalid", "errorMessage"]),
        _createVNode(GgmsMultiSelect, {
          modelValue: lifecycleStage.value,
          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((lifecycleStage).value = $event)),
          label: "Lifecycle Stages",
          placeholder: "+ Add Lifecycle Stage",
          display: "chip",
          options: lifecycleStages.value,
          optionLabel: "displayName",
          optionValue: "_id",
          invalid: _unref(isInvalid)('lifecycleStages', error.value),
          errorMessage: _unref(hasErrorMessage)('lifecycleStages', error.value),
          classes: "w-full",
          "open-direction": "above"
        }, null, 8, ["modelValue", "options", "invalid", "errorMessage"]),
        _createVNode(GgmsMultiSelect, {
          modelValue: contactRole.value,
          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((contactRole).value = $event)),
          label: "Contact Roles",
          placeholder: "+ Add Contact Role",
          display: "chip",
          options: contactRoles.value,
          optionLabel: "displayName",
          optionValue: "_id",
          invalid: _unref(isInvalid)('contactRoles', error.value),
          errorMessage: _unref(hasErrorMessage)('contactRoles', error.value),
          classes: "w-full",
          "open-direction": "above"
        }, null, 8, ["modelValue", "options", "invalid", "errorMessage"]),
        _createVNode(GgmsMultiSelect, {
          modelValue: salesStatus.value,
          "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((salesStatus).value = $event)),
          label: "Sales Statuses",
          placeholder: "+ Add Sales Status",
          display: "chip",
          options: salesStatuses.value,
          optionLabel: "displayName",
          optionValue: "_id",
          invalid: _unref(isInvalid)('salesStatuses', error.value),
          errorMessage: _unref(hasErrorMessage)('salesStatuses', error.value),
          classes: "w-full",
          "open-direction": "above"
        }, null, 8, ["modelValue", "options", "invalid", "errorMessage"])
      ], 32)
    ]),
    _: 1
  }, 8, ["open", "onKeydown"]))
}
}

})